import { Alert, Button } from '@cimpress/react-components';
import { useCallback, useEffect, useState } from 'react';
import { type CustomInputType, getPreviewUrlForPdf, getRenderingUrlDocument } from '../../api';
import { type Card, CardBuilder } from '../common';
import type { PreviewDataLoader } from '../common/previewCard/types';
import { useCustomUploadCard } from './UseCustomUploadCard';

export type ImageCardData = {
    fileUrl: string;
    title: string;
    type: CustomInputType;
};

export type UseImageCardType = {
    imageCardData: Readonly<ImageCardData[]>;
    documentLoaders: Record<string, PreviewDataLoader<'document'>>;
    processImage: (fileUrl: string, previewImageUrl: string, type: CustomInputType) => Promise<void>;
    onChange: (url: string | undefined) => void;
    fileUrl: string | undefined;
};

export function useUploadAndPresetCards({
    imageCardData,
    documentLoaders,
    processImage,
    onChange,
    fileUrl,
}: UseImageCardType) {
    const [imageCards, setImageCards] = useState<Card[]>([]);

    const createImageCard = useCallback(
        async (imageCardData: ImageCardData): Promise<Card> => {
            let previewUrl = '';
            const { type, fileUrl, title } = imageCardData;
            if (type === 'unknown') {
                return CardBuilder.ofType('childComponent')
                    .withTitle('Unknown File Type')
                    .withChildComponent(() => (
                        <div
                            style={{
                                height: '300px',
                                padding: '12px',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '8px',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <Alert
                                status="danger"
                                message="Unable to determine file type. Please ensure you've uploaded a valid image, PDF, or document file."
                                dismissible={false}
                            />
                        </div>
                    ))
                    .build();
            }
            if (type === 'document') {
                previewUrl = getRenderingUrlDocument(fileUrl, 0);
            }
            if (type === 'pdf') {
                previewUrl = getPreviewUrlForPdf(fileUrl);
            }
            if (type === 'image') {
                previewUrl = fileUrl;
            }

            const documentLoader = documentLoaders[fileUrl];

            if (documentLoader) {
                return CardBuilder.ofType('document').withTitle(title).withLoader(documentLoader).build();
            }

            return CardBuilder.ofType('childComponent')
                .withTitle(title)
                .withChildComponent(() => (
                    <div
                        style={{
                            height: '300px',
                            padding: '12px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '8px',
                            alignItems: 'center',
                        }}
                    >
                        <div
                            style={{
                                flexGrow: 1,
                                width: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                overflow: 'hidden',
                            }}
                        >
                            <img
                                src={previewUrl}
                                alt={title}
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'contain',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px',
                                }}
                            />
                        </div>
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                width: '100%',
                                marginTop: 'auto',
                            }}
                        >
                            <Button onClick={() => processImage(fileUrl, previewUrl, type)}>Enrich Cimdoc</Button>
                        </div>
                    </div>
                ))
                .build();
        },
        [documentLoaders, processImage],
    );

    useEffect(() => {
        (async () => {
            const imageCardPromises = imageCardData.map(createImageCard);
            setImageCards(await Promise.all(imageCardPromises));
        })();
    }, [createImageCard, documentLoaders, imageCardData, processImage]);

    const customUploadCard = useCustomUploadCard({
        onChange,
        createImageCard,
        fileUrl,
    });

    const rows = customUploadCard ? [customUploadCard, ...imageCards] : imageCards;

    return rows;
}
